import React, { useEffect } from 'react';
import {
  MenuItem,
  TextField,
  DropdownField,
  IconButton,
  Button,
  Link,
  useBreakpoint,
} from '@one-thd/sui-atomic-components';
import { useForm, Controller, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { Search, Close } from '@one-thd/sui-icons';
import { TB2BCreditCardSearchFormValues } from '../../../models/b2b/payments';
import { searchCreditCardValidationSchema } from './validation';

import {
  B2BCreditCardsFilterType,
  B2BCreditCardsFilterTypePlaceHolder,
} from '../../../api/b2b/payments';
import { usePayments } from '../../../hooks/use-payments';
import {
  CardSortByLabel,
  cardSortByMap,
  cardSortInputValues,
} from '../../../constants/sort';
import {
  CreditCardFilterTypes,
  CreditCardStatusFilterMapping,
  CreditCardStatusFilterTypes,
  CreditCardTypesTextMapping,
} from '../../../constants/credit-card';
import {
  setCardSearchAnalytics,
  setCardSearchSortPostPageErrorAnalytics,
  setCardSortAnalytics,
} from '../../../utils/payments-analytics';

interface CreditCardSortSearchBarProps {
  triggerGetPaymentCards: (
    pageNumber: number,
    fType: B2BCreditCardsFilterType,
    fValue: string,
    sortValue: CardSortByLabel | null,
    ascendingOrder: boolean | null,
    pageSize?: number,
  ) => void;
  onResetResults: (isListView: boolean) => void;
}

export const CreditCardSortSearchBar = ({
  triggerGetPaymentCards,
  onResetResults,
}: CreditCardSortSearchBarProps) => {
  const {
    isSearchActive,
    setAlertMessage,
    currentSortByValue,
    submittedSearchValue,
    submittedSearchType,
    isListView,
  } = usePayments();

  const screen = useBreakpoint('lg');
  const isSmallScreen = screen.lesser;

  const formMethods = useForm<TB2BCreditCardSearchFormValues>({
    resolver: yupResolver(searchCreditCardValidationSchema),
    mode: 'onTouched',
    defaultValues: {
      filterType: B2BCreditCardsFilterType.NOTHING,
      filterValue: '',
    },
  });

  const {
    control,
    formState: { errors: formErrors, isValid },
    reset,
    trigger,
    watch,
  } = formMethods;
  const filterValue = watch('filterValue');
  const filterType = watch('filterType');

  const executeSort = (
    sortBy: CardSortByLabel | null,
    ascendingOrder: boolean,
  ) => {
    triggerGetPaymentCards(
      1,
      submittedSearchType,
      submittedSearchValue,
      sortBy,
      ascendingOrder,
    );
  };

  useEffect(() => {
    if (formErrors.filterValue?.message) {
      setCardSearchSortPostPageErrorAnalytics(formErrors.filterValue.message);
    }
  }, [formErrors.filterValue?.message]);

  const renderTextInput = () => {
    return (
      <div className="sui-grid sui-grid-cols-7">
        <div
          className={
            'sui-grid ' + (filterValue ? 'sui-col-span-6' : 'sui-col-span-7')
          }
        >
          <Controller
            name="filterValue"
            control={control}
            render={({ field }) => {
              const { ref, value, name, onChange, ...rest } = field;

              const onChangeRevised = (
                event: React.ChangeEvent<HTMLInputElement>,
              ) => {
                let newFilterValue = event.currentTarget.value;
                // if exp date and the first two digits have been entered, automatically include the slash
                if (
                  filterType === B2BCreditCardsFilterType.expDate &&
                  newFilterValue.length === 2 &&
                  !filterValue.includes('/')
                ) {
                  newFilterValue += '/';
                }
                onChange(newFilterValue);
                trigger('filterValue');
              };

              return (
                <TextField
                  disabled={filterType === B2BCreditCardsFilterType.NOTHING}
                  status={formErrors?.filterValue ? 'error' : undefined}
                  statusMessage={formErrors?.filterValue?.message}
                  fullWidth
                  value={value}
                  name={name}
                  onChange={onChangeRevised}
                  placeholder={B2BCreditCardsFilterTypePlaceHolder[filterType]}
                  id="searchField"
                  InputProps={{
                    style: {
                      backgroundColor:
                        'var(--UI-Background-Fill-Background-Subtle, #F4F4F4)',
                    },
                    inputProps: {
                      ...rest,
                    },
                    endAdornment: value ? (
                      <IconButton
                        onClick={() => {
                          reset({ filterValue: '', filterType });
                        }}
                        icon={Close}
                        iconSize="small"
                        aria-label="Clear"
                      />
                    ) : (
                      <Search color="medium" size="small" />
                    ),
                  }}
                />
              );
            }}
          />
        </div>
        {filterValue && (
          <div className="sui-ml-2" style={{ marginTop: '8px' }}>
            <Button
              disabled={!isValid}
              onClick={() => {
                setAlertMessage(null);
                triggerGetPaymentCards(
                  1,
                  filterType,
                  filterValue,
                  currentSortByValue.sortBy,
                  currentSortByValue.ascendingOrder,
                );
                setCardSearchAnalytics(isListView, filterType, 'user entry');
              }}
            >
              <Search size="small" />
            </Button>
          </div>
        )}
      </div>
    );
  };

  const renderSearchFieldDropdown = () => {
    if (filterType === B2BCreditCardsFilterType.status) {
      return (
        <Controller
          name="filterValue"
          key={filterType}
          control={control}
          render={({ field }) => {
            const { value, onChange } = field;
            return (
              <DropdownField
                fullWidth
                value={value}
                dropdownProps={{
                  onChange: (
                    event: React.MouseEvent,
                    newValue: CreditCardStatusFilterTypes,
                  ) => {
                    onChange(event);
                    triggerGetPaymentCards(
                      1,
                      filterType,
                      newValue,
                      currentSortByValue.sortBy,
                      !!currentSortByValue.ascendingOrder,
                    );
                    setCardSearchAnalytics(isListView, filterType, newValue);
                  },
                  placeholder: B2BCreditCardsFilterTypePlaceHolder[filterType],
                }}
              >
                {CreditCardStatusFilterMapping.map((menuValue) => (
                  <MenuItem key={menuValue} value={menuValue}>
                    {menuValue}
                  </MenuItem>
                ))}
              </DropdownField>
            );
          }}
        />
      );
    }
    if (filterType === B2BCreditCardsFilterType.cardBrand) {
      return (
        <Controller
          name="filterValue"
          key={filterType}
          control={control}
          render={({ field }) => {
            const { value, onChange } = field;
            return (
              <DropdownField
                fullWidth
                value={value}
                dropdownProps={{
                  onChange: (
                    event: React.MouseEvent,
                    newValue: CreditCardFilterTypes,
                  ) => {
                    onChange(event);
                    triggerGetPaymentCards(
                      1,
                      filterType,
                      newValue.valueOf(),
                      currentSortByValue.sortBy,
                      !!currentSortByValue.ascendingOrder,
                    );
                    setCardSearchAnalytics(isListView, filterType, newValue);
                  },
                  placeholder: B2BCreditCardsFilterTypePlaceHolder[filterType],
                  maxItems: 8,
                  'data-testid': 'user-entry-card-choice-dropdown',
                }}
              >
                {Object.entries(CreditCardTypesTextMapping).map(
                  ([key, menuValue]) => (
                    <MenuItem key={key} value={key}>
                      {menuValue}
                    </MenuItem>
                  ),
                )}
              </DropdownField>
            );
          }}
        />
      );
    }
    return '';
  };

  const renderSearchField = () => {
    switch (filterType) {
      case B2BCreditCardsFilterType.NOTHING:
      case B2BCreditCardsFilterType.nickName:
      case B2BCreditCardsFilterType.cardLast4Digits:
      case B2BCreditCardsFilterType.expDate:
      case B2BCreditCardsFilterType.citiID:
        return renderTextInput();
      case B2BCreditCardsFilterType.status:
      case B2BCreditCardsFilterType.cardBrand:
        return renderSearchFieldDropdown();
      default:
        return '';
    }
  };

  const handleClearSearch = () => {
    const fType = B2BCreditCardsFilterType.NOTHING;
    const fValue = '';
    onResetResults(isListView);
    reset({ filterType: fType, filterValue: fValue });
    setAlertMessage(null);
  };

  return (
    <div className="sui-grid sm:sui-grid-cols-1 md:sui-grid-cols-2 2xl:sui-grid-cols-4 sui-w-full sui-gap-x-1 sui-mb-6">
      <FormProvider {...formMethods}>
        <div className="sui-grid sui-grid-rows-1 sui-flex">
          <Controller
            name="filterType"
            control={control}
            render={({ field }) => {
              const { value, onChange } = field;
              return (
                <DropdownField
                  value={value}
                  label="Search By"
                  id="searchByField"
                  dropdownProps={{
                    onChange: (
                      event: React.MouseEvent,
                      newValue: B2BCreditCardsFilterType,
                    ) => {
                      reset({ filterValue: '', filterType: newValue });
                      onChange(event);
                    },
                    placeholder: 'Choose a Search Field',
                    'data-testid': 'searchByFieldTest',
                  }}
                >
                  <MenuItem
                    disabled
                    value={B2BCreditCardsFilterType.NOTHING.valueOf()}
                  >
                    Choose a Search Field
                  </MenuItem>
                  <MenuItem
                    value={B2BCreditCardsFilterType.cardBrand.valueOf()}
                  >
                    Card Type
                  </MenuItem>
                  <MenuItem value={B2BCreditCardsFilterType.nickName.valueOf()}>
                    Nickname
                  </MenuItem>
                  <MenuItem value={B2BCreditCardsFilterType.expDate.valueOf()}>
                    Exp. Date
                  </MenuItem>
                  <MenuItem
                    value={B2BCreditCardsFilterType.cardLast4Digits.valueOf()}
                  >
                    Card Number
                  </MenuItem>
                  <MenuItem value={B2BCreditCardsFilterType.status.valueOf()}>
                    Status
                  </MenuItem>
                  <MenuItem value={B2BCreditCardsFilterType.citiID.valueOf()}>
                    Citi Account ID
                  </MenuItem>
                </DropdownField>
              );
            }}
          />
          <div className="sui-pt-4 sui-grid sui-grid-rows-1 sui-hidden md:sui-block">
            {isSearchActive && (
              // eslint-disable-next-line jsx-a11y/anchor-is-valid
              <Link
                component="button"
                decoration="underline"
                variant="body-base"
                onClick={handleClearSearch}
              >
                Clear Search
              </Link>
            )}
          </div>
        </div>
        <div className="sui-pt-3 md:sui-pt-0 md:sui-pt-0 sui-mt-0 md:sui-mt-5 2xl:sui-mr-4">
          {renderSearchField()}
          <div
            className={`sui-grid sui-grid-rows-1 ${
              isSearchActive ? 'sui-pt-4' : ''
            } sui-pb-4 sui-border-b-1 sui-border-primary sui-border-solid sui-block md:sui-hidden`}
          >
            {isSearchActive && (
              // eslint-disable-next-line jsx-a11y/anchor-is-valid
              <Link
                component="button"
                decoration="underline"
                variant="body-base"
                onClick={handleClearSearch}
              >
                Clear Search
              </Link>
            )}
          </div>
        </div>
      </FormProvider>

      {(!isListView || isSmallScreen) && (
        <>
          <div className="sui-grid sui-grid-rows-1 sui-mb-0 sui-pt-4 md:sui-pt-0 2xl:sui-ml-4 sui-pl-0 ">
            <DropdownField
              value={currentSortByValue.sortBy}
              label="Sort By"
              id="sortByField"
              dropdownProps={{
                onChange: (
                  _event: React.MouseEvent,
                  sortType: CardSortByLabel,
                ) => {
                  executeSort(sortType, true);
                  setCardSortAnalytics(
                    isListView,
                    sortType,
                    cardSortByMap[sortType || 'Card Type'].orders[0],
                  );
                },
                placeholder: 'Choose a Sort Field',
                'data-testid': 'sortByFieldTest',
              }}
            >
              {cardSortInputValues.map((sortBy) => (
                <MenuItem key={sortBy} value={sortBy}>
                  {sortBy}
                </MenuItem>
              ))}
            </DropdownField>
          </div>
          <div
            className={`sui-grid sui-grid-rows-1 sui-mb-0 sui-pt-5 md:sui-pt-0 ${
              currentSortByValue.ascendingOrder === null
                ? 'sui-hidden md:sui-block'
                : ''
            }`}
          >
            <DropdownField
              value={
                currentSortByValue.ascendingOrder === null
                  ? null
                  : ['desc', 'asc'][+currentSortByValue.ascendingOrder]
              }
              label="Order"
              id="sortByValueField"
              fullWidth
              dropdownProps={{
                onChange: (
                  _event: React.MouseEvent,
                  ascendingOrder: 'asc' | 'desc',
                ) => {
                  executeSort(
                    currentSortByValue.sortBy,
                    ascendingOrder === 'asc',
                  );
                  setCardSortAnalytics(
                    isListView,
                    currentSortByValue.sortBy
                      ? currentSortByValue.sortBy.toString()
                      : '',
                    cardSortByMap[currentSortByValue.sortBy || 'Card Type']
                      .orders[ascendingOrder === 'asc' ? 0 : 1],
                  );
                },
                placeholder: 'Choose a Sort Order',
                'data-testid': 'sortByValueFieldTest',
              }}
            >
              {cardSortByMap[
                currentSortByValue.sortBy || 'Card Type'
              ].orders.map((order, index) => (
                <MenuItem key={order} value={index === 0 ? 'asc' : 'desc'}>
                  {order}
                </MenuItem>
              ))}
            </DropdownField>
          </div>
        </>
      )}
    </div>
  );
};
